import { NotLoggedInError } from "./error"
import { TonWalletUtils } from "./secrets"

async function safeParseBody(response: Response) {
  const body = await response.text()
  try {
    return JSON.parse(body)
  } catch (e) {
    return body
  }
}

export class ApiError extends Error {
  public errorCode: number
  public statusCode: number
  public responseBody: string | { [key: string]: any }
  
  constructor(
    errorCode: number,
    statusCode: number,
    responseBody: string | { [key: string]: any },
  ) {
    super(`Back-end error ${statusCode} ${JSON.stringify(responseBody)}`)
    this.errorCode = errorCode
    this.statusCode = statusCode
    this.responseBody = responseBody
  }
}

const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000'

export class Client {
  baseUrl = apiUrl
  defaultHeader = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Client-Type': 'telegram-mini-app',
  }

  private async handleErrorResponse(responseBody: any) {
    throw new ApiError(
      responseBody.errorCode || 500,
      responseBody.statusCode || 500,
      responseBody,
    )
  }

  private async handleResponse(response: Response) {
    if (response.status < 400) {
      const responseBody = await safeParseBody(response)
      return responseBody
    } else if (response.status >= 400) {
      const responseBody = await safeParseBody(response)
      await this.handleErrorResponse(responseBody)
    }
  }

  private async getToken() {
    try {
      return await TonWalletUtils.getValidToken()
    } catch (error) {
      if (error instanceof NotLoggedInError) {
        if (['Token invalid'].includes(error.message)) {
          await TonWalletUtils.refreshToken()
          return await TonWalletUtils.getValidToken()
        }
      }
      throw error
    }
  }

  doUnauthenticatedGet = async (url: string, headers?: any) => {
    const response = await fetch(`${this.baseUrl}${url}`, {
      method: 'GET',
      headers: {
        ...this.defaultHeader,
        ...(headers || {}),
      },
    })
    return this.handleResponse(response)
  }

  doUnauthenticatedPost = async (
    url: string,
    body: { [key: string]: any },
    headers?: any,
  ) => {
    const response = await fetch(`${this.baseUrl}${url}`, {
      method: 'POST',
      headers: {
        ...this.defaultHeader,
        ...(headers || {}),
      },
      body: JSON.stringify(body),
    })
    return this.handleResponse(response)
  }

  doPost = async <T = any>(
    url: string,
    body: { [key: string]: any },
    headers?: any,
  ): Promise<T> => {
    const token = await this.getToken()

    // Don't stringify if it's FormData
    const processedBody = body instanceof FormData ? body : JSON.stringify(body)

    // Don't include Content-Type: application/json if it's FormData
    const defaultHeaders =
      body instanceof FormData
        ? {
            Accept: 'application/json',
          }
        : this.defaultHeader

    const response = await fetch(`${this.baseUrl}${url}`, {
      method: 'POST',
      headers: {
        ...defaultHeaders,
        ...(headers || {}),
        Authorization: `Bearer ${token}`,
      },
      body: processedBody,
    })
    return this.handleResponse(response)
  }

  doGet = async <T = any>(
    url: string,
    headers?: any,
  ): Promise<T> => {
    const token = await this.getToken()
    const response = await fetch(`${this.baseUrl}${url}`, {
      method: 'GET',
      headers: {
        ...this.defaultHeader,
        ...(headers || {}),
        Authorization: `Bearer ${token}`,
      },
    })
    return this.handleResponse(response)
  }

  doDelete = async (
    url: string,
    headers?: any,
  ) => {
    const token = await this.getToken()
    const response = await fetch(`${this.baseUrl}${url}`, {
      method: 'DELETE',
      headers: {
        ...this.defaultHeader,
        ...(headers || {}),
        Authorization: `Bearer ${token}`,
      },
    })
    return this.handleResponse(response)
  }

  doPut = async (
    url: string,
    body: { [key: string]: any },
    headers?: any,
  ) => {
    const token = await this.getToken()
    const response = await fetch(`${this.baseUrl}${url}`, {
      method: 'PUT',
      headers: {
        ...this.defaultHeader,
        ...(headers || {}),
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    })
    return this.handleResponse(response)
  }

  doPatch = async (
    url: string,
    body: { [key: string]: any },
    headers?: any,
  ) => {
    const token = await this.getToken()
    const response = await fetch(`${this.baseUrl}${url}`, {
      method: 'PATCH',
      headers: {
        ...this.defaultHeader,
        ...(headers || {}),
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    })
    return this.handleResponse(response)
  }
}

export const client = new Client()
